import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AdditionalDataService from 'api/additional-data/AdditionalDataService';
import { AdditionalDataDto } from 'api/additional-data/models/AdditionalDataDto';
import { DataTableColumn } from 'common/components/dataTable/models';
import { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import { formatAdditionalDataValue } from 'common/utils/additionalDataUtils';

interface RowWithAdditionalData {
    additionalDataValues?: Record<string, string>;
}

export const useAdditionalDataListingColumns = <TRow extends RowWithAdditionalData>(
    linkType: string
): { columns: DataTableColumn<TRow>[]; listingColumns: ListingTableColumn<TRow>[]; isLoading: boolean } => {
    const { t } = useTranslation();
    const [fields, setFields] = useState<AdditionalDataDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        AdditionalDataService.getListingFieldsByType(linkType)
            .then(setFields)
            .catch(() => setFields([]))
            .finally(() => setIsLoading(false));
    }, [linkType]);

    const columns = useMemo((): DataTableColumn<TRow>[] => {
        return fields.map(field => ({
            id: `customField_${field.id}`,
            field: `customField_${field.id}` as keyof TRow,
            name: field.designation || '',
            hideOrderButton: true,
            width: 130,
            hideOn: ['sm', 'md'],
            renderCell: row => <>{formatAdditionalDataValue(
                row.additionalDataValues?.[field.id!],
                field.type,
                t
            )}</>,
            exportValue: row => formatAdditionalDataValue(
                row.additionalDataValues?.[field.id!],
                field.type,
                t
            ),
        }));
    }, [fields, t]);

    const listingColumns = useMemo((): ListingTableColumn<TRow>[] => {
        return fields.map(field => ({
            name: field.designation || '',
            width: '130px',
            hideOn: ['sm', 'md'],
            renderCell: row => <>{formatAdditionalDataValue(
                row.additionalDataValues?.[field.id!],
                field.type,
                t
            )}</>,
        }));
    }, [fields, t]);

    return { columns, listingColumns, isLoading };
};
